// ./src/utils/api/AudioLibraryService.ts
//
// The audio block's MUSIC & SOUNDS LIBRARY (A2d #72 follow-up) — searched through OUR API, which holds the white label's
// keys (migration 1990), rate-limits each caller and caches every search for a day. The browser never sees a key.
//
// ⚠ Both reads are about the EVENT whose page is being edited: its white label decides which libraries are offered and
//   whose keys pay for them. Without one only Openverse is offered (it needs no key).
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";

const ERRORS = `
  code
  errors {
    name
    errors
  }
`;

const AUDIO_LIBRARY_SOURCES_QUERY = gql`
  query audioLibrarySources($params: AudioLibrarySourcesInput) {
    audioLibrarySources(params: $params) {
      ${ERRORS}
      data {
        source
        name
        url
        kinds
      }
    }
  }
`;

const AUDIO_LIBRARY_SEARCH_QUERY = gql`
  query audioLibrarySearch($params: AudioLibrarySearchInput) {
    audioLibrarySearch(params: $params) {
      ${ERRORS}
      data {
        items {
          id
          source
          provider
          title
          creator
          creatorUrl
          pageUrl
          url
          license
          licenseVersion
          licenseLabel
          licenseUrl
          durationSec
          credit
        }
        total
        hasMore
        page
      }
    }
  }
`;

export type AudioLibrarySourceId = "openverse" | "freesound" | "jamendo";
export type AudioLibraryKind = "music" | "sounds";

/** A library the picker may search for this event. Mirrors `AudioLibrarySource`. */
export interface AudioLibrarySource {
  source: AudioLibrarySourceId;
  name: string;
  url: string;
  kinds: AudioLibraryKind[];
}

/** One track. Mirrors `AudioLibraryItem` — every one carries a licence that allows commercial use, and its credit. */
export interface AudioLibraryItem {
  id: string;
  source: AudioLibrarySourceId;
  provider: string;
  title: string;
  creator?: string | null;
  creatorUrl?: string | null;
  pageUrl?: string | null;
  url: string;
  license: string;
  licenseVersion?: string | null;
  licenseLabel: string;
  licenseUrl?: string | null;
  durationSec?: number | null;
  credit: string;
}

export interface AudioLibraryPage {
  items: AudioLibraryItem[];
  total: number;
  hasMore: boolean;
  page: number;
}

export class AudioLibraryService {
  static async sources(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(AUDIO_LIBRARY_SOURCES_QUERY, { params: { eventId } });
  }

  static async search(params: { eventId: string; source: AudioLibrarySourceId; kind: AudioLibraryKind; query: string; page: number }): Promise<OpResult> {
    return ApiService.executeQuery(AUDIO_LIBRARY_SEARCH_QUERY, { params });
  }
}
